import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { TabNavigationMode } from '@/common/types'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './navigation-menu.scss?inline'
import type DapDSNavigationMenuItem from './navigation-menu-item.component'

/**
 * `dap-ds-navigation-menu`
 * @summary A navigation menu for website/application navigation. Supports nested dropdowns with unlimited depth.
 * This is a NAVIGATION component, not an application menu (like dropdowns or context menus).
 * Uses semantic HTML (`<nav>`) and proper navigation ARIA attributes.
 *
 * @element dap-ds-navigation-menu
 * @title - Navigation Menu
 *
 * @property {string} activeHref - The currently active href for highlighting active navigation items with aria-current="page". Defaults to window.location.pathname if not provided.
 * @property {'horizontal' | 'vertical'} orientation - The orientation of the navigation menu. Default is 'horizontal'.
 * @property {boolean} fullWidth - Whether the navigation menu should take full width of the screen. Default is false.
 * @property {boolean} allowMultipleOpen - Whether multiple dropdown branches can stay open simultaneously (accordion mode). Default is true.
 * @property {'group' | 'items'} tabMode - Controls how Tab key navigates through top-level items. Default is 'group'. Group mode will have the first item in the tab order, and items mode will have all items in the tab order.
 *
 * @event {{ href: string, event: Event }} dds-navigation-item-click - Fired when a navigation item is clicked.
 *
 * @slot - The navigation menu list and items.
 *
 * @csspart base - The main navigation menu container (nav element).
 *
 * @cssproperty --dds-navigation-menu-item-gap - The gap between navigation menu items. (default: var(--dds-spacing-100)).
 *
 * @accessibility
 * - Uses semantic `<nav>` element (no menu/menubar roles)
 * - Active items marked with `aria-current="page"`
 * - Keyboard navigation: Tab, Arrow keys, Enter, Escape
 * - Screen readers announce as navigation landmark
 */
@customElement('dap-ds-navigation-menu')
export default class DapDSNavigationMenu extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  /** @ignore */
  @query('slot') defaultSlot: HTMLSlotElement

  /** The currently active href for highlighting active navigation items. */
  @property({ type: String }) activeHref?: string

  /** The orientation of the navigation menu.
   * @type {'horizontal' | 'vertical'}
   */
  @property({ reflect: true }) orientation: 'horizontal' | 'vertical' =
    'horizontal'

  /** Whether the navigation menu should take full width of the screen. */
  @property({ type: Boolean }) fullWidth = false

  /** Whether multiple dropdown branches can stay open simultaneously (accordion mode). */
  @property({ type: Boolean }) allowMultipleOpen = true

  /**
   * Controls how Tab key navigates through top-level items.
   * - 'group' (default): Roving tab index — only one item in the tab order, arrow keys move between items.
   * - 'items': All top-level items have tabindex="0" — Tab moves through each item individually.
   * @type {'group' | 'items'}
   */
  @property({ type: String }) tabMode: TabNavigationMode = 'group'

  connectedCallback() {
    super.connectedCallback()

    if (!this.activeHref) {
      this.activeHref = window.location.pathname
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    if (changedProperties.has('activeHref')) {
      this._updateActiveHref()
    }

    if (changedProperties.has('fullWidth')) {
      this._updateChildFullWidth()
    }

    if (changedProperties.has('orientation')) {
      this._updateOrientation()
    }

    if (changedProperties.has('tabMode')) {
      this._applyTabMode()
      this._updateTabMode()
    }
  }

  async firstUpdated(changedProperties: Map<string, unknown>) {
    super.firstUpdated(changedProperties)
    this._updateChildFullWidth()
    this._updateOrientation()

    // Wait for child items to be fully initialized
    await this.updateComplete
    requestAnimationFrame(() => {
      this._handleSlotChange()
    })
  }

  /** Handle keyboard navigation for top-level menubar items only */
  private handleKeyDown(event: KeyboardEvent) {
    const items = this.getAllItems()
    const activeItem = this.getCurrentItem()

    // Only handle if we have items
    if (items.length === 0) return

    // Check if event is from a nested submenu item (not a direct child)
    // If so, let the submenu handle it
    const target = event.target as HTMLElement

    // Check if target is inside a nested menu item (level > 0)
    const allMenuItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    let isFromNestedItem = false

    allMenuItems.forEach(item => {
      // If the item is not a direct child (it's nested), and contains the target
      if (
        !items.includes(item as DapDSNavigationMenuItem) &&
        item.contains(target)
      ) {
        isFromNestedItem = true
      }
    })

    if (isFromNestedItem) {
      // Event is from nested submenu, let the menu-item handle it
      return
    }

    // Enter or Space: activate current item (click trigger or open submenu)
    if (event.key === 'Enter' || event.key === ' ') {
      if (activeItem) {
        const trigger = activeItem.querySelector(
          '[slot="trigger"]',
        ) as HTMLElement

        // Check if trigger is a link
        const isLink =
          trigger?.tagName.toLowerCase() === 'a' ||
          trigger?.tagName.toLowerCase() === 'dap-ds-link'

        // For links with Enter key, let the browser handle it naturally
        // Only prevent default for Space key or non-link elements
        if (!isLink || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
        }

        // If item has submenu, open it
        if (activeItem.hasNestedItems || activeItem.hasContent) {
          event.preventDefault()
          event.stopPropagation()
          if (!activeItem.opened) {
            activeItem.showDropDown()
          }
        } else if (!isLink || event.key === ' ') {
          // For non-links or Space key, simulate click
          trigger?.click()
        }
        // For links with Enter, browser default handles navigation
      }
    }

    // Arrow keys: navigate between items or open submenus
    else if (
      [
        'ArrowDown',
        'ArrowUp',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      let index = activeItem ? items.indexOf(activeItem) : 0

      // Horizontal menubar (top-level navigation)
      if (this.orientation === 'horizontal') {
        // ArrowLeft/Right: navigate between top-level items
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          event.stopPropagation()
          index--
          if (index < 0) index = items.length - 1
          this.setCurrentItem(items[index])
          items[index].focus()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          event.stopPropagation()
          index++
          if (index > items.length - 1) index = 0
          this.setCurrentItem(items[index])
          items[index].focus()
        }
        // ArrowDown/ArrowUp: open submenu if current item has one, then move focus into it
        else if (
          (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
          activeItem
        ) {
          if (activeItem.hasNestedItems || activeItem.hasContent) {
            event.preventDefault()
            event.stopPropagation()
            if (!activeItem.opened) {
              activeItem.showDropDown()
              this.updateComplete.then(() => {
                if (event.key === 'ArrowDown') {
                  activeItem.focusFirstDropdownChild()
                } else {
                  activeItem.focusLastDropdownChild()
                }
              })
            } else {
              // Menu already open — move focus into it
              if (event.key === 'ArrowDown') {
                activeItem.focusFirstDropdownChild()
              } else {
                activeItem.focusLastDropdownChild()
              }
            }
          }
        }
        // Home/End
        else if (event.key === 'Home') {
          event.preventDefault()
          event.stopPropagation()
          this.setCurrentItem(items[0])
          items[0].focus()
        } else if (event.key === 'End') {
          event.preventDefault()
          event.stopPropagation()
          this.setCurrentItem(items[items.length - 1])
          items[items.length - 1].focus()
        }
      }
      // Vertical menubar
      else {
        // ArrowUp/Down: navigate between top-level items
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          index--
          if (index < 0) index = items.length - 1
          this.setCurrentItem(items[index])
          items[index].focus()
        } else if (event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          index++
          if (index > items.length - 1) index = 0
          this.setCurrentItem(items[index])
          items[index].focus()
        }
        // ArrowRight: open submenu if current item has one
        else if (event.key === 'ArrowRight' && activeItem) {
          if (activeItem.hasNestedItems || activeItem.hasContent) {
            event.preventDefault()
            event.stopPropagation()
            if (!activeItem.opened) {
              activeItem.showDropDown()
              this.updateComplete.then(() => {
                activeItem.focusFirstDropdownChild()
              })
            } else {
              activeItem.focusFirstDropdownChild()
            }
          }
        }
        // Home/End
        else if (event.key === 'Home') {
          event.preventDefault()
          event.stopPropagation()
          this.setCurrentItem(items[0])
          items[0].focus()
        } else if (event.key === 'End') {
          event.preventDefault()
          event.stopPropagation()
          this.setCurrentItem(items[items.length - 1])
          items[items.length - 1].focus()
        }
      }
    }
  }

  private handleMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement

    if (this.isMenuItem(target)) {
      this.setCurrentItem(target as DapDSNavigationMenuItem)
    }
  }

  private handleSlotChange() {
    const items = this.getAllItems()

    // Reset the roving tab index when the slotted items change
    if (items.length > 0) {
      this.setCurrentItem(items[0])
    }

    // Update properties on all items
    this._updateActiveHref()
    this._updateChildFullWidth()
    this._updateOrientation()
    this._updateTabMode()
    this._applyTabMode()
  }

  private isMenuItem(item: HTMLElement) {
    return item.tagName.toLowerCase() === 'dap-ds-navigation-menu-item'
  }

  /** Gets all slotted menu items, ignoring disabled elements */
  getAllItems() {
    return [...this.defaultSlot.assignedElements({ flatten: true })].filter(
      (el: Element) => {
        const htmlEl = el as HTMLElement
        if (htmlEl.inert || !this.isMenuItem(htmlEl)) {
          return false
        }
        return true
      },
    ) as DapDSNavigationMenuItem[]
  }

  /**
   * Gets the current menu item (the one marked with `data-nav-current`).
   * The menu item may or may not have focus, but for keyboard interaction purposes it's considered the "active" item.
   */
  getCurrentItem() {
    return this.getAllItems().find(i => i.hasAttribute('data-nav-current'))
  }

  /**
   * Sets the current menu item. In 'group' mode (default), applies roving tabindex so only the
   * active item's trigger is in the sequential tab order (tabindex="0"). In 'items' mode, all
   * top-level items remain in the tab order — only the data-nav-current marker is updated.
   */
  setCurrentItem(item: DapDSNavigationMenuItem) {
    const items = this.getAllItems()
    if (this.tabMode === 'items') {
      // items mode: keep all tabindex="0", only track current for keyboard nav
      items.forEach(i => {
        if (i === item) {
          i.setAttribute('data-nav-current', '')
        } else {
          i.removeAttribute('data-nav-current')
        }
      })
    } else {
      // group mode: roving tabindex — only active item in tab order
      items.forEach(i => {
        i.setRovingTabIndex(i === item)
      })
    }
  }

  private _updateChildFullWidth() {
    if (this.fullWidth) {
      this._updatePropertyRecursive('fullWidth', true)
    }
  }

  /** Recursively update activeHref property for all navigation items. */
  private _updateActiveHref() {
    this._updatePropertyRecursive('activeHref', this.activeHref)
  }

  /** Recursively update orientation property for all navigation items. */
  private _updateOrientation() {
    this._updatePropertyRecursive('orientation', this.orientation)
  }

  /** Update tabMode property for all top-level navigation items. */
  private _updateTabMode() {
    this.getAllItems().forEach(i => {
      i.tabMode = this.tabMode
    })
  }

  /**
   * Applies tab behavior based on the current tabMode:
   * - 'items': All top-level item triggers get tabindex="0".
   * - 'group': Restore roving tabindex — only the current/first item gets tabindex="0".
   */
  private _applyTabMode() {
    const items = this.getAllItems()
    if (items.length === 0) return

    if (this.tabMode === 'items') {
      // Determine which item is current before modifying state
      const currentItem =
        items.find(i => i.hasAttribute('data-nav-current')) ?? items[0]
      // Give all items tabindex="0" (setRovingTabIndex also sets data-nav-current on all)
      items.forEach(i => i.setRovingTabIndex(true))
      // Restore single data-nav-current marker
      items.forEach(i => {
        if (i !== currentItem) i.removeAttribute('data-nav-current')
      })
    } else {
      // Restore roving tabindex: only current (or first) item in tab order
      const current =
        items.find(i => i.hasAttribute('data-nav-current')) ?? items[0]
      items.forEach(i => i.setRovingTabIndex(i === current))
    }
  }

  /** Helper method to recursively update properties on nested items. */
  private _updatePropertyRecursive(
    property: string,
    value: any,
    element: Element = this,
  ) {
    const navigationItems = element.querySelectorAll(
      ':scope > dap-ds-navigation-menu-item',
    ) as NodeListOf<DapDSNavigationMenuItem>

    navigationItems.forEach(item => {
      ;(item as any)[property] = value

      this._updatePropertyRecursive(property, value, item)
    })
  }

  private _handleNavigationItemClick = (event: Event) => {
    const customEvent = event as CustomEvent
    // Re-emit the event to parent components if needed
    this.emit('dds-navigation-item-click', customEvent.detail)
  }

  private _handleDropdownOpen = (event: Event) => {
    // If multiple dropdowns can be open, don't close others
    if (this.allowMultipleOpen) {
      return
    }

    const openingItem = event.target as DapDSNavigationMenuItem

    // Only close siblings at the same level, not nested items
    const parent = openingItem.parentElement
    const siblings =
      parent?.querySelectorAll(':scope > dap-ds-navigation-menu-item') || []

    siblings.forEach(item => {
      if (item !== openingItem && (item as DapDSNavigationMenuItem).opened) {
        ;(item as DapDSNavigationMenuItem).hideDropDown()
      }
    })
  }

  private _handleSlotChange = () => {
    this.handleSlotChange()
  }

  /**
   * In `tabMode="items"`, keep `data-nav-current` in sync with the element that
   * received focus so that arrow-key navigation always targets the right item.
   */
  private _handleFocusIn = (event: FocusEvent) => {
    if (this.tabMode !== 'items') return

    const target = event.target as HTMLElement
    const items = this.getAllItems()

    const focusedItem = items.find(
      item => item === target || item.contains(target),
    )

    if (focusedItem) {
      this.setCurrentItem(focusedItem)
    }
  }

  render() {
    return html`
      <nav
        part="base"
        aria-orientation=${this.orientation === 'vertical'
          ? 'vertical'
          : 'horizontal'}
        class=${clsx('navigation-menu', {
          'navigation-menu--vertical': this.orientation === 'vertical',
        })}
        aria-label=${ifDefined(this.ariaLabel || 'Main navigation')}>
        <slot
          @slotchange=${this._handleSlotChange}
          @click=${this._handleNavigationItemClick}
          @dds-navigation-dropdown-open=${this._handleDropdownOpen}
          @keydown=${this.handleKeyDown}
          @mousedown=${this.handleMouseDown}
          @focusin=${this._handleFocusIn}></slot>
      </nav>
    `
  }
}
